import { useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

/**
 * OtpInput
 *  props:
 *    - value: string[6]
 *    - onChange: (idx, val) => void
 *    - onPaste: (e) => void
 *    - disabled: boolean
 *    - status: 'idle' | 'pending' | 'valid' | 'invalid'
 */
export default function OtpInput({ value, onChange, onPaste, disabled = false, status = "idle", testIdPrefix = "otp" }) {
  const refs = useRef([]);

  useEffect(() => {
    if (!disabled) refs.current[0]?.focus();
  }, [disabled]);

  const handleKeyDown = (i, e) => {
    if (e.key === "Backspace" && !value[i] && i > 0) {
      refs.current[i - 1]?.focus();
    }
    if (e.key === "ArrowLeft" && i > 0) refs.current[i - 1]?.focus();
    if (e.key === "ArrowRight" && i < 5) refs.current[i + 1]?.focus();
  };

  const handleChange = (i, v) => {
    const digit = v.replace(/\D/g, "").slice(-1);
    onChange(i, digit);
    if (digit && i < 5) refs.current[i + 1]?.focus();
  };

  const boxCls =
    status === "valid" ? "ok" : status === "invalid" ? "bad" : "";

  return (
    <div className="w-full">
      <div className="flex items-center gap-2 sm:gap-2.5" onPaste={onPaste}>
        {value.map((d, i) => (
          <motion.input
            key={i}
            ref={(el) => (refs.current[i] = el)}
            value={d}
            onChange={(e) => handleChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            disabled={disabled}
            maxLength={1}
            inputMode="numeric"
            className={`otp-box ${boxCls}`}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.04 }}
            data-testid={`${testIdPrefix}-${i}`}
          />
        ))}
      </div>
      <div className="mt-2 flex items-center gap-1.5 text-[12px]">
        {status === "pending" && (
          <span className="flex items-center gap-1.5 text-[hsl(220,15%,46%)]">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Verifying code…
          </span>
        )}
        {status === "valid" && (
          <span className="flex items-center gap-1.5 font-medium text-[hsl(152,60%,32%)]">
            <CheckCircle2 className="h-3.5 w-3.5" /> Code verified
          </span>
        )}
        {status === "invalid" && (
          <span className="flex items-center gap-1.5 font-medium text-[hsl(356,78%,45%)]">
            <XCircle className="h-3.5 w-3.5" /> Invalid code. Try again.
          </span>
        )}
      </div>
    </div>
  );
}
